/**
 * Unified settings hook.
 *
 * Abstracts the split between server-authoritative settings (persisted in
 * `settings.json` on the server, fetched via `server.getConfig`) and
 * client-only settings (persisted in localStorage).
 *
 * Consumers use `useSettings(selector)` to read, and `useUpdateSettings()` to
 * write. The hook transparently routes reads/writes to the correct backing
 * store.
 */
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { ServerSettings, ServerSettingsPatch } from "@cmmd-forge/contracts";
import {
  type ClientSettings,
  DEFAULT_CLIENT_SETTINGS,
  DEFAULT_UNIFIED_SETTINGS,
  UnifiedSettings,
} from "@cmmd-forge/contracts/settings";
import { Struct } from "effect";
import { deepMerge } from "@cmmd-forge/shared/Struct";
import { applySettingsUpdated, getServerConfig, useServerSettings } from "~/rpc/serverState";
import { getPrimaryEnvironmentConnection } from "~/environments/runtime/service";
import { getClientSettings, setClientSettings } from "~/clientPersistenceApi";

const CLIENT_SETTINGS_PERSISTENCE_ERROR_SCOPE = "[CLIENT_SETTINGS]";

const clientSettingsListeners = new Set<() => void>();
let clientSettingsSnapshot = DEFAULT_CLIENT_SETTINGS;
let clientSettingsHydrated = false;
let clientSettingsHydrationPromise: Promise<void> | null = null;

/**
 * Whether the persisted client settings have been read into memory. The
 * first-run gate holds on "pending" so a fresh browser never mounts before the
 * onboarding flag is known, and shows recovery on "failed" instead of reading
 * defaults as "never onboarded".
 */
export type ClientSettingsHydrationStatus = "pending" | "ready" | "failed";

let clientSettingsHydrationStatus: ClientSettingsHydrationStatus = "pending";

function setClientSettingsHydrationStatus(status: ClientSettingsHydrationStatus): void {
  if (clientSettingsHydrationStatus === status) {
    return;
  }
  clientSettingsHydrationStatus = status;
  emitClientSettingsChange();
}

function emitClientSettingsChange() {
  for (const listener of clientSettingsListeners) {
    listener();
  }
}

function getClientSettingsSnapshot(): ClientSettings {
  return clientSettingsSnapshot;
}

function replaceClientSettingsSnapshot(settings: ClientSettings): void {
  clientSettingsSnapshot = settings;
  emitClientSettingsChange();
}

function subscribeClientSettings(listener: () => void): () => void {
  clientSettingsListeners.add(listener);
  void hydrateClientSettings();
  return () => {
    clientSettingsListeners.delete(listener);
  };
}

async function hydrateClientSettings(): Promise<void> {
  if (clientSettingsHydrated) {
    return;
  }
  if (clientSettingsHydrationPromise) {
    return clientSettingsHydrationPromise;
  }

  const nextHydration = (async () => {
    try {
      const persistedSettings = await getClientSettings();
      if (persistedSettings) {
        replaceClientSettingsSnapshot(persistedSettings);
      }
      clientSettingsHydrated = true;
      setClientSettingsHydrationStatus("ready");
    } catch (error) {
      // Stay unhydrated so an explicit retry can re-run the read; a broken
      // store must not read as "never onboarded".
      console.error(`${CLIENT_SETTINGS_PERSISTENCE_ERROR_SCOPE} hydrate failed`, error);
      setClientSettingsHydrationStatus("failed");
    }
  })();

  const hydrationPromise = nextHydration.finally(() => {
    if (clientSettingsHydrationPromise === hydrationPromise) {
      clientSettingsHydrationPromise = null;
    }
  });
  clientSettingsHydrationPromise = hydrationPromise;

  return clientSettingsHydrationPromise;
}

// ── Key sets for routing patches ─────────────────────────────────────

const SERVER_SETTINGS_KEYS = new Set<string>(Struct.keys(ServerSettings.fields));

function splitPatch(patch: Partial<UnifiedSettings>): {
  serverPatch: ServerSettingsPatch;
  clientPatch: Partial<ClientSettings>;
} {
  const serverPatch: Record<string, unknown> = {};
  const clientPatch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (SERVER_SETTINGS_KEYS.has(key)) {
      serverPatch[key] = value;
    } else {
      clientPatch[key] = value;
    }
  }
  return {
    serverPatch: serverPatch as ServerSettingsPatch,
    clientPatch: clientPatch as Partial<ClientSettings>,
  };
}

/**
 * Await the persisted client settings read. Resolves once hydration has
 * settled (ready or failed), so writers can merge against real persisted
 * state instead of defaults.
 */
export function ensureClientSettingsHydrated(): Promise<void> {
  return hydrateClientSettings();
}

let clientSettingsWriteQueue: Promise<void> = Promise.resolve();

/**
 * Serialize a client-settings write. Every write path funnels through this
 * queue and derives its result from the snapshot the previous write left
 * behind, so a pending write cannot overwrite newer settings or persist a
 * stale value. Each turn first waits for hydration and refuses to write
 * unless it reached "ready": persisting after a failed read would overwrite
 * every saved preference with defaults. A failed write leaves the snapshot
 * untouched and the queue keeps moving for later writes.
 */
function enqueueClientSettingsWrite<T>(write: () => Promise<T>): Promise<T> {
  const run = clientSettingsWriteQueue.then(async () => {
    await ensureClientSettingsHydrated();
    if (getClientSettingsHydrationStatus() !== "ready") {
      throw new Error(
        `${CLIENT_SETTINGS_PERSISTENCE_ERROR_SCOPE} hydration failed; refusing to persist settings.`,
      );
    }
    return write();
  });
  clientSettingsWriteQueue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * Persist a client-settings update derived from the current snapshot.
 *
 * Unlike `useUpdateSettings`, this awaits the write and updates the in-memory
 * snapshot only after it succeeds, so a failed persistence cannot read back as
 * saved. The welcome wizard's completion flow needs that guarantee: it must
 * not close the wizard when the flag was not actually stored.
 */
export function persistClientSettingsUpdate(
  update: (current: ClientSettings) => ClientSettings,
): Promise<void> {
  return enqueueClientSettingsWrite(async () => {
    const next = update(getClientSettingsSnapshot());
    await setClientSettings(next);
    replaceClientSettingsSnapshot(next);
  });
}

/**
 * Read the hydration status reactively. "pending" until the first read
 * settles, "ready" once persisted settings (or their absence) are in memory,
 * "failed" when the store could not be read at all.
 */
export function useClientSettingsHydrationStatus(): ClientSettingsHydrationStatus {
  return useSyncExternalStore(
    subscribeClientSettings,
    getClientSettingsHydrationStatus,
    getClientSettingsHydrationStatus,
  );
}

export function getClientSettingsHydrationStatus(): ClientSettingsHydrationStatus {
  return clientSettingsHydrationStatus;
}

// ── Hooks ────────────────────────────────────────────────────────────

/**
 * Whether the client-settings store has finished hydrating from persisted
 * storage. The pre-hydration snapshot is just the schema defaults, so gates
 * that switch a whole surface (the sidebar v2 escape hatch) must wait on
 * this rather than resolve against defaults, or every user would mount the
 * default surface and then swap it out once persisted settings land.
 */
function useClientSettingsHydrated(): boolean {
  return useSyncExternalStore(
    subscribeClientSettings,
    () => clientSettingsHydrated,
    () => false,
  );
}

/**
 * Whether the legacy sidebar (the v1 per-project thread tree) replaces the
 * default sidebar v2.
 *
 * Held at the default sidebar until client settings hydrate: the
 * pre-hydration snapshot is just the schema defaults, so resolving against
 * it could mount one sidebar and then swap it out once persisted settings
 * land, remounting the whole tree for everyone instead of only for legacy
 * opt-ins.
 */
export function useLegacySidebarEnabled(): boolean {
  const settingsHydrated = useClientSettingsHydrated();
  // Called unconditionally: skipping it while `settingsHydrated` is false
  // would change this component's hook order at the hydration flip and
  // throw. The gate below only decides the returned value.
  const legacySidebarEnabled = useSettings((s) => s.legacySidebarEnabled);
  return settingsHydrated && legacySidebarEnabled;
}

/**
 * Read merged settings. Selector narrows the subscription so components
 * only re-render when the slice they care about changes.
 */

export function useSettings<T = UnifiedSettings>(selector?: (s: UnifiedSettings) => T): T {
  const serverSettings = useServerSettings();
  const clientSettings = useSyncExternalStore(
    subscribeClientSettings,
    getClientSettingsSnapshot,
    () => DEFAULT_CLIENT_SETTINGS,
  );

  const merged = useMemo<UnifiedSettings>(
    () => ({
      ...serverSettings,
      ...clientSettings,
    }),
    [clientSettings, serverSettings],
  );

  return useMemo(() => (selector ? selector(merged) : (merged as T)), [merged, selector]);
}

/**
 * Returns an updater that routes each key to the correct backing store.
 *
 * Server keys are optimistically patched in atom-backed server state, then
 * persisted via RPC. Client keys go through client persistence.
 */
export function useUpdateSettings() {
  const updateSettings = useCallback((patch: Partial<UnifiedSettings>) => {
    const { serverPatch, clientPatch } = splitPatch(patch);

    if (Object.keys(serverPatch).length > 0) {
      const currentServerConfig = getServerConfig();
      if (currentServerConfig) {
        applySettingsUpdated(deepMerge(currentServerConfig.settings, serverPatch));
      }
      // Fire-and-forget RPC — push will reconcile on success
      void getPrimaryEnvironmentConnection().client.server.updateSettings(serverPatch);
    }

    if (Object.keys(clientPatch).length > 0) {
      // The merge happens inside the queued turn so it derives from whatever
      // snapshot the previous write left behind, never from a value captured
      // while another write was still in flight.
      void enqueueClientSettingsWrite(async () => {
        const next = { ...getClientSettingsSnapshot(), ...clientPatch };
        replaceClientSettingsSnapshot(next);
        await setClientSettings(next).catch((error) => {
          console.error(`${CLIENT_SETTINGS_PERSISTENCE_ERROR_SCOPE} persist failed`, error);
        });
      }).catch((error) => {
        console.error(`${CLIENT_SETTINGS_PERSISTENCE_ERROR_SCOPE} persist failed`, error);
      });
    }
  }, []);

  const resetSettings = useCallback(() => {
    updateSettings(DEFAULT_UNIFIED_SETTINGS);
  }, [updateSettings]);

  return {
    updateSettings,
    resetSettings,
  };
}

export function __resetClientSettingsPersistenceForTests(): void {
  clientSettingsSnapshot = DEFAULT_CLIENT_SETTINGS;
  clientSettingsHydrated = false;
  clientSettingsHydrationPromise = null;
  clientSettingsHydrationStatus = "pending";
  clientSettingsListeners.clear();
}
